import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useNotification } from '../contexts/NotificationContext'
import { supabase } from '../lib/supabase'

interface ImportTradesProps {
  onClose: () => void
  onSuccess?: () => void
  appearance?: 'classic' | 'studio'
}

interface ParsedTrade {
  market?: string
  symbol?: string
  trade_type?: string
  long_short?: string
  quantity?: number
  contract_size?: number
  entry_date?: string
  entry_price?: number
  stop_loss?: number
  take_profit?: number
  rr?: number
  exit_date?: string
  exit_price?: number
  trade_setup?: string
  notes?: string
  strategy_id?: string
  currency?: string
  position_size?: number
  pnl?: number
  pnl_percentage?: number
  time_interval?: string
  entry_setup_image?: string
  exit_setup_image?: string
  tags?: string[]
  is_public?: boolean
  account_id?: string
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export default function ImportTrades({ onClose, onSuccess, appearance = 'classic' }: ImportTradesProps) {
  const studio = appearance === 'studio'
  const { t } = useTranslation('trading')
  const { user } = useAuth()
  const { showSuccess } = useNotification()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pastedData, setPastedData] = useState('')
  const [preview, setPreview] = useState<ParsedTrade[]>([])
  const [showPreview, setShowPreview] = useState(false)
  const [accounts, setAccounts] = useState<Array<{ id: string; name?: string; platform: string }>>([])

  useEffect(() => {
    if (!user) return
    supabase
      .from('accounts')
      .select('id, name, platform')
      .eq('user_id', user.id)
      .then(({ data }) => setAccounts(data ?? []))
  }, [user])

  const parseData = (data: string): ParsedTrade[] => {
    try {
      // Split by newlines
      const lines = data.trim().split('\n')
      if (lines.length < 2) {
        throw new Error('At least 2 lines (header + data) are required')
      }

      // First line is headers
      const headers = lines[0].split('\t').map(h => h.trim().toLowerCase())
      
      // Map common header names to our database fields
      const headerMap: { [key: string]: string } = {
        'symbol': 'symbol',
        'market': 'market',
        'direction': 'long_short',
        'long_short': 'long_short',
        'long/short': 'long_short',
        'entry date': 'entry_date',
        'entry_date': 'entry_date',
        'entrydate': 'entry_date',
        'entry price': 'entry_price',
        'entry_price': 'entry_price',
        'entryprice': 'entry_price',
        'exit date': 'exit_date',
        'exit_date': 'exit_date',
        'exitdate': 'exit_date',
        'exit price': 'exit_price',
        'exit_price': 'exit_price',
        'exitprice': 'exit_price',
        'stop loss': 'stop_loss',
        'stop_loss': 'stop_loss',
        'stoploss': 'stop_loss',
        'sl': 'stop_loss',
        'take profit': 'take_profit',
        'take_profit': 'take_profit',
        'takeprofit': 'take_profit',
        'tp': 'take_profit',
        'rr': 'rr',
        'risk/reward': 'rr',
        'quantity': 'quantity',
        'qty': 'quantity',
        'contract size': 'contract_size',
        'contract_size': 'contract_size',
        'contractsize': 'contract_size',
        'p&l': 'pnl',
        'pnl': 'pnl',
        'profit': 'pnl',
        'p&l %': 'pnl_percentage',
        'pnl %': 'pnl_percentage',
        'pnl_percentage': 'pnl_percentage',
        'pnl%': 'pnl_percentage',
        'currency': 'currency',
        'strategy': 'strategy_id',
        'strategy_id': 'strategy_id',
        'trade setup': 'trade_setup',
        'trade_setup': 'trade_setup',
        'setup': 'trade_setup',
        'notes': 'notes',
        'note': 'notes',
        'trade type': 'trade_type',
        'trade_type': 'trade_type',
        'tradetype': 'trade_type',
        'type': 'trade_type',
        'position size': 'position_size',
        'position_size': 'position_size',
        'positionsize': 'position_size',
        'time interval': 'time_interval',
        'time_interval': 'time_interval',
        'timeinterval': 'time_interval',
        'interval': 'time_interval',
        'entry setup image': 'entry_setup_image',
        'entry_setup_image': 'entry_setup_image',
        'exit setup image': 'exit_setup_image',
        'exit_setup_image': 'exit_setup_image',
        'tags': 'tags',
        'tag': 'tags',
        'feed': 'is_public',
        'is_public': 'is_public',
        'ispublic': 'is_public',
        'public': 'is_public',
        'account': 'account_id',
        'account_id': 'account_id',
        'accountid': 'account_id',
      }

      // Map headers to database fields
      const mappedHeaders = headers.map(h => headerMap[h] || null)

      // Check if we have required fields
      const hasSymbol = mappedHeaders.includes('symbol')
      const hasEntryPrice = mappedHeaders.includes('entry_price')
      
      if (!hasSymbol || !hasEntryPrice) {
        throw new Error('Required columns missing: Symbol and Entry Price are required')
      }

      // Parse data rows
      const trades: ParsedTrade[] = []
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split('\t')
        if (values.length === 0 || values.every(v => !v.trim())) {
          continue // Skip empty lines
        }

        const trade: ParsedTrade = {}
        
        for (let j = 0; j < values.length && j < mappedHeaders.length; j++) {
          const field = mappedHeaders[j]
          const value = values[j]?.trim()
          
          if (!field || !value) continue

          try {
            switch (field) {
              case 'symbol':
                trade.symbol = value
                break
              case 'market':
                const marketValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
                if (['Commodity', 'Crypto', 'Forex', 'Stock'].includes(marketValue)) {
                  trade.market = marketValue
                } else {
                  trade.market = 'Forex' // default
                }
                break
              case 'long_short':
                const direction = value.toLowerCase()
                if (direction.includes('long') || direction === 'l') {
                  trade.long_short = 'long'
                } else if (direction.includes('short') || direction === 's') {
                  trade.long_short = 'short'
                }
                break
              case 'trade_type':
                const type = value.toLowerCase()
                if (type.includes('spot')) {
                  trade.trade_type = 'spot'
                } else if (type.includes('future') || type.includes('futures')) {
                  trade.trade_type = 'future'
                }
                break
              case 'entry_date':
              case 'exit_date':
                // Try to parse date
                if (value && value !== '-') {
                  try {
                    let date: Date | null = null
                    
                    // Try dd.mm.yyyy or mm.dd.yyyy format (e.g., 27.09.2025 or 09.27.2025)
                    const dotDateMatch = value.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
                    if (dotDateMatch) {
                      const first = parseInt(dotDateMatch[1], 10)
                      const second = parseInt(dotDateMatch[2], 10)
                      const year = parseInt(dotDateMatch[3], 10)
                      
                      // Smart detection: if first > 12, it must be day (dd.mm.yyyy)
                      // if second > 12, it must be month (mm.dd.yyyy)
                      // otherwise, try both and see which is valid
                      if (first > 12) {
                        // Definitely dd.mm.yyyy
                        date = new Date(year, second - 1, first)
                      } else if (second > 12) {
                        // Definitely mm.dd.yyyy
                        date = new Date(year, first - 1, second)
                      } else {
                        // Ambiguous - try dd.mm.yyyy first (more common in European format)
                        date = new Date(year, second - 1, first)
                        // If invalid, try mm.dd.yyyy
                        if (isNaN(date.getTime())) {
                          date = new Date(year, first - 1, second)
                        }
                      }
                    } else {
                      // Try standard Date parsing for other formats (ISO, etc.)
                      date = new Date(value)
                    }
                    
                    if (date && !isNaN(date.getTime())) {
                      trade[field] = date.toISOString()
                    }
                  } catch (e) {
                    // Invalid date, skip
                  }
                }
                break
              case 'entry_price':
              case 'exit_price':
              case 'stop_loss':
              case 'take_profit':
              case 'quantity':
              case 'contract_size':
              case 'rr':
              case 'position_size':
              case 'pnl':
                // Parse numeric values with smart decimal separator handling
                if (value && value !== '-') {
                  try {
                    // Remove any spaces
                    let cleanValue = value.replace(/\s/g, '')
                    
                    // Count dots and commas
                    const dotCount = (cleanValue.match(/\./g) || []).length
                    const commaCount = (cleanValue.match(/,/g) || []).length
                    
                    if (dotCount === 0 && commaCount === 0) {
                      // No separators, just parse as integer
                      const num = parseFloat(cleanValue)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (dotCount === 1 && commaCount === 0) {
                      // Single dot - treat as decimal separator
                      const num = parseFloat(cleanValue)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (commaCount === 1 && dotCount === 0) {
                      // Single comma - treat as decimal separator
                      cleanValue = cleanValue.replace(',', '.')
                      const num = parseFloat(cleanValue)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (dotCount > 0 && commaCount > 0) {
                      // Both separators present - determine which is thousands and which is decimal
                      // Usually the last separator is the decimal separator
                      const lastDotIndex = cleanValue.lastIndexOf('.')
                      const lastCommaIndex = cleanValue.lastIndexOf(',')
                      
                      if (lastDotIndex > lastCommaIndex) {
                        // Last separator is dot, so dot is decimal, comma is thousands
                        cleanValue = cleanValue.replace(/,/g, '')
                        const num = parseFloat(cleanValue)
                        if (!isNaN(num)) {
                          trade[field] = num
                        }
                      } else {
                        // Last separator is comma, so comma is decimal, dot is thousands
                        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.')
                        const num = parseFloat(cleanValue)
                        if (!isNaN(num)) {
                          trade[field] = num
                        }
                      }
                    } else if (dotCount > 1) {
                      // Multiple dots - last one is decimal, others are thousands
                      const parts = cleanValue.split('.')
                      const integerPart = parts.slice(0, -1).join('')
                      const decimalPart = parts[parts.length - 1]
                      const num = parseFloat(integerPart + '.' + decimalPart)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (commaCount > 1) {
                      // Multiple commas - last one is decimal, others are thousands
                      const parts = cleanValue.split(',')
                      const integerPart = parts.slice(0, -1).join('')
                      const decimalPart = parts[parts.length - 1]
                      const num = parseFloat(integerPart + '.' + decimalPart)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    }
                  } catch (e) {
                    console.error(`Error parsing numeric value ${value}:`, e)
                  }
                }
                break
              case 'pnl_percentage':
                // Parse percentage values with smart decimal separator handling
                if (value && value !== '-') {
                  try {
                    // Remove any spaces and % sign
                    let cleanValue = value.replace(/\s/g, '').replace(/%/g, '')
                    
                    // Count dots and commas
                    const dotCount = (cleanValue.match(/\./g) || []).length
                    const commaCount = (cleanValue.match(/,/g) || []).length
                    
                    if (dotCount === 0 && commaCount === 0) {
                      // No separators, just parse as integer
                      const num = parseFloat(cleanValue)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (dotCount === 1 && commaCount === 0) {
                      // Single dot - treat as decimal separator
                      const num = parseFloat(cleanValue)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (commaCount === 1 && dotCount === 0) {
                      // Single comma - treat as decimal separator
                      cleanValue = cleanValue.replace(',', '.')
                      const num = parseFloat(cleanValue)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (dotCount > 0 && commaCount > 0) {
                      // Both separators present - determine which is thousands and which is decimal
                      // Usually the last separator is the decimal separator
                      const lastDotIndex = cleanValue.lastIndexOf('.')
                      const lastCommaIndex = cleanValue.lastIndexOf(',')
                      
                      if (lastDotIndex > lastCommaIndex) {
                        // Last separator is dot, so dot is decimal, comma is thousands
                        cleanValue = cleanValue.replace(/,/g, '')
                        const num = parseFloat(cleanValue)
                        if (!isNaN(num)) {
                          trade[field] = num
                        }
                      } else {
                        // Last separator is comma, so comma is decimal, dot is thousands
                        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.')
                        const num = parseFloat(cleanValue)
                        if (!isNaN(num)) {
                          trade[field] = num
                        }
                      }
                    } else if (dotCount > 1) {
                      // Multiple dots - last one is decimal, others are thousands
                      const parts = cleanValue.split('.')
                      const integerPart = parts.slice(0, -1).join('')
                      const decimalPart = parts[parts.length - 1]
                      const num = parseFloat(integerPart + '.' + decimalPart)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    } else if (commaCount > 1) {
                      // Multiple commas - last one is decimal, others are thousands
                      const parts = cleanValue.split(',')
                      const integerPart = parts.slice(0, -1).join('')
                      const decimalPart = parts[parts.length - 1]
                      const num = parseFloat(integerPart + '.' + decimalPart)
                      if (!isNaN(num)) {
                        trade[field] = num
                      }
                    }
                  } catch (e) {
                    console.error(`Error parsing percentage value ${value}:`, e)
                  }
                }
                break
              case 'currency':
              case 'trade_setup':
              case 'notes':
              case 'time_interval':
              case 'entry_setup_image':
              case 'exit_setup_image':
                if (value && value !== '-') {
                  trade[field] = value
                }
                break
              case 'tags':
                if (value && value !== '-') {
                  // Support comma-separated or space-separated tags
                  const tags = value.split(/[,\s]+/).map(t => t.trim()).filter(Boolean)
                  if (tags.length > 0) {
                    trade.tags = tags.slice(0, 5) // Max 5 tags
                  }
                }
                break
              case 'is_public':
                if (value && value !== '-') {
                  const v = value.toLowerCase()
                  trade.is_public = ['true', 'yes', '1', 'public', 'evet'].includes(v)
                }
                break
              case 'account_id':
                if (value && value !== '-') {
                  trade.account_id = value.trim() // Store raw value; resolve to UUID at import time
                }
                break
              default:
                break
            }
          } catch (e) {
            console.error(`Error parsing field ${field}:`, e)
          }
        }

        // Set defaults for required fields
        if (!trade.market) trade.market = 'Forex'
        if (!trade.long_short) trade.long_short = 'long'
        if (!trade.trade_type) trade.trade_type = 'spot'
        if (!trade.contract_size) trade.contract_size = 1

        trades.push(trade)
      }

      return trades
    } catch (err: any) {
      throw new Error(`Failed to parse data: ${err.message}`)
    }
  }

  const handlePreview = () => {
    setError(null)
    try {
      if (!pastedData.trim()) {
        setError('Please paste data to preview')
        return
      }

      const parsed = parseData(pastedData)
      if (parsed.length === 0) {
        setError('No valid data found to import')
        return
      }

      setPreview(parsed)
      setShowPreview(true)
    } catch (err: any) {
      setError(err.message || 'Failed to parse data')
    }
  }

  const handleImport = async () => {
    if (!user) {
      setError('You must be logged in to import trades')
      return
    }

    if (preview.length === 0) {
      setError('No data to import')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Resolve account ref (name/platform) to UUID
      const resolveAccountId = (ref: string): string | null => {
        if (!ref?.trim()) return null
        const r = ref.trim()
        if (UUID_REGEX.test(r)) return r
        const match = accounts.find(
          a => (a.name?.toLowerCase() === r.toLowerCase()) || (a.platform?.toLowerCase() === r.toLowerCase())
        )
        return match?.id ?? null
      }

      // Prepare trades for insertion
      const tradesToInsert = preview.map(trade => {
        const tradeData: any = {
          user_id: user.id,
          market: trade.market || 'Forex',
          symbol: trade.symbol || '',
          trade_type: trade.trade_type || 'spot',
          long_short: trade.long_short || 'long',
        }

        // Add optional fields
        if (trade.entry_date) tradeData.entry_date = trade.entry_date
        if (trade.entry_price !== undefined) tradeData.entry_price = trade.entry_price
        if (trade.exit_date) tradeData.exit_date = trade.exit_date
        if (trade.exit_price !== undefined) tradeData.exit_price = trade.exit_price
        if (trade.stop_loss !== undefined) tradeData.stop_loss = trade.stop_loss
        if (trade.take_profit !== undefined) tradeData.take_profit = trade.take_profit
        if (trade.quantity !== undefined) tradeData.quantity = trade.quantity
        if (trade.contract_size !== undefined) tradeData.contract_size = trade.contract_size
        if (trade.rr !== undefined) tradeData.rr = trade.rr
        if (trade.position_size !== undefined) tradeData.position_size = trade.position_size
        if (trade.pnl !== undefined) tradeData.pnl = trade.pnl
        if (trade.pnl_percentage !== undefined) tradeData.pnl_percentage = trade.pnl_percentage
        if (trade.currency) tradeData.currency = trade.currency
        if (trade.trade_setup) tradeData.trade_setup = trade.trade_setup
        if (trade.notes) tradeData.notes = trade.notes
        if (trade.time_interval) tradeData.time_interval = trade.time_interval
        if (trade.entry_setup_image) tradeData.entry_setup_image = trade.entry_setup_image
        if (trade.exit_setup_image) tradeData.exit_setup_image = trade.exit_setup_image
        if (Array.isArray(trade.tags) && trade.tags.length > 0) tradeData.tags = trade.tags.slice(0, 5)
        if (trade.is_public !== undefined) tradeData.is_public = trade.is_public
        if (trade.account_id) {
          const resolved = resolveAccountId(trade.account_id)
          if (resolved) tradeData.account_id = resolved
        }

        // Calculate missing values if possible
        if (!tradeData.position_size && tradeData.quantity && tradeData.entry_price) {
          tradeData.position_size = tradeData.quantity * tradeData.entry_price
        }

        if (!tradeData.pnl && tradeData.exit_price && tradeData.entry_price && tradeData.quantity) {
          if (tradeData.long_short === 'long') {
            tradeData.pnl = (tradeData.exit_price - tradeData.entry_price) * tradeData.quantity
          } else {
            tradeData.pnl = (tradeData.entry_price - tradeData.exit_price) * tradeData.quantity
          }
        }

        if (!tradeData.pnl_percentage && tradeData.pnl && tradeData.position_size) {
          tradeData.pnl_percentage = (tradeData.pnl / tradeData.position_size) * 100
        }

        if (!tradeData.rr && tradeData.entry_price && tradeData.stop_loss && tradeData.take_profit) {
          if (tradeData.long_short === 'long') {
            const risk = tradeData.entry_price - tradeData.stop_loss
            const reward = tradeData.take_profit - tradeData.entry_price
            if (risk > 0) {
              tradeData.rr = reward / risk
            }
          } else {
            const risk = tradeData.stop_loss - tradeData.entry_price
            const reward = tradeData.entry_price - tradeData.take_profit
            if (risk > 0) {
              tradeData.rr = reward / risk
            }
          }
        }

        return tradeData
      })

      // Validate that all trades have required fields
      const invalidTrades = tradesToInsert.filter(t => !t.symbol || t.entry_price === undefined)
      if (invalidTrades.length > 0) {
        throw new Error(`${invalidTrades.length} trades are missing required fields (Symbol or Entry Price)`)
      }

      // Insert trades in batches to avoid timeout
      const batchSize = 50
      let successCount = 0

      for (let i = 0; i < tradesToInsert.length; i += batchSize) {
        const batch = tradesToInsert.slice(i, i + batchSize)
        const { error: insertError } = await supabase
          .from('trades')
          .insert(batch)

        if (insertError) {
          throw insertError
        }
        successCount += batch.length
      }

      // Success
      if (onSuccess) {
        onSuccess()
      }
      showSuccess(`Successfully imported ${successCount} trades!`)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to import trades')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={studio ? 'at-quick-trade-backdrop' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto'}>
      <div
        className={studio
          ? 'at-import-trades'
          : 'rounded-2xl p-5 max-w-4xl w-full mx-4 my-8 shadow-xl max-h-[90vh] overflow-y-auto'}
        style={studio ? undefined : { backgroundColor: '#FBFBFB' }}
      >
        <div
          className={studio ? 'at-qt-title at-import-trades-head' : 'flex justify-between items-center mb-5 sticky top-0 pb-3 border-b'}
          style={studio ? undefined : { backgroundColor: '#FBFBFB' }}
        >
          <h2 className={studio ? undefined : 'text-xl font-semibold text-gray-900'}>
            {studio ? (
              <span>
                {t('importTrades.title')}
                <small>Paste tab-separated rows from Excel or Google Sheets.</small>
              </span>
            ) : t('importTrades.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className={studio ? undefined : 'text-gray-400 hover:text-gray-600 transition-colors'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={studio ? 'at-import-trades-body space-y-5' : 'space-y-5'}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Instructions:</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Copy your data from Excel or Google Sheets (including headers)</li>
              <li>The first row should contain column headers</li>
              <li>Required columns: <strong>Symbol</strong> and <strong>Entry Price</strong></li>
              <li>Paste the data in the text area below</li>
              <li>Click "Preview" to see how the data will be imported</li>
              <li>Click "Import" to add the trades to your journal</li>
            </ol>
            <div className="mt-3 text-xs text-blue-700">
              <p className="font-semibold mb-1">Supported columns:</p>
              <p>Symbol, Market, Direction (Long/Short), Entry Date, Entry Price, Exit Date, Exit Price, 
              Stop Loss, Take Profit, RR, Quantity, Contract Size, P&L, P&L %, Currency, Strategy, 
              Trade Setup, Notes, Trade Type, Position Size, Time Interval, Tags, Feed (true/false), Account (name or platform)</p>
            </div>
          </div>

          {/* Paste Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Paste your data here (tab-separated)
            </label>
            <textarea
              value={pastedData}
              onChange={(e) => {
                setPastedData(e.target.value)
                setShowPreview(false)
                setError(null)
              }}
              rows={10}
              className={studio
                ? 'at-qt-input at-qt-notes font-mono'
                : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono'}
              placeholder="Symbol&#9;Market&#9;Direction&#9;Entry Date&#9;Entry Price&#9;Exit Price&#10;BTC/USD&#9;Crypto&#9;Long&#9;2024-01-01&#9;50000&#9;52000"
            />
          </div>

          {/* Preview Button */}
          {!showPreview && (
            <button
              onClick={handlePreview}
              disabled={!pastedData.trim()}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Preview Data
            </button>
          )}

          {/* Preview Table */}
          {showPreview && preview.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Preview ({preview.length} trades)</h3>
              <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-96">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Symbol</th>
                      <th className="px-2 py-1 text-left">Market</th>
                      <th className="px-2 py-1 text-left">Direction</th>
                      <th className="px-2 py-1 text-left">Entry Date</th>
                      <th className="px-2 py-1 text-right">Entry Price</th>
                      <th className="px-2 py-1 text-right">Exit Price</th>
                      <th className="px-2 py-1 text-right">Quantity</th>
                      <th className="px-2 py-1 text-left">Tags</th>
                      <th className="px-2 py-1 text-left">Feed</th>
                      <th className="px-2 py-1 text-left">Account</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {preview.map((trade, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-2 py-1">{trade.symbol || '-'}</td>
                        <td className="px-2 py-1">{trade.market || '-'}</td>
                        <td className="px-2 py-1">
                          <span className={`px-1 py-0.5 rounded text-xs ${
                            trade.long_short === 'long' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {trade.long_short?.toUpperCase() || '-'}
                          </span>
                        </td>
                        <td className="px-2 py-1">
                          {trade.entry_date ? new Date(trade.entry_date).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-2 py-1 text-right">{trade.entry_price?.toFixed(2) || '-'}</td>
                        <td className="px-2 py-1 text-right">{trade.exit_price?.toFixed(2) || '-'}</td>
                        <td className="px-2 py-1 text-right">{trade.quantity?.toFixed(4) || '-'}</td>
                        <td className="px-2 py-1">{Array.isArray(trade.tags) ? trade.tags.join(', ') : '-'}</td>
                        <td className="px-2 py-1">{trade.is_public ? 'Yes' : '-'}</td>
                        <td className="px-2 py-1">{trade.account_id || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowPreview(false)
                    setPreview([])
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Edit Data
                </button>
                <button
                  onClick={handleImport}
                  disabled={loading}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {loading ? 'Importing...' : `Import ${preview.length} Trades`}
                </button>
              </div>
            </div>
          )}

          {/* Cancel Button (only shown when not in preview mode) */}
          {!showPreview && (
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
